<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
    <div class="bg-white rounded-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
        <h2 class="text-xl font-bold text-gray-900">
          {{ isEdit ? 'Editar Empresa' : 'Nova Empresa' }}
        </h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Informações Básicas -->
          <div class="md:col-span-2">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h3>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa *</label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
            <input
              v-model="form.slug"
              type="text"
              required
              :disabled="isEdit"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 disabled:bg-gray-100"
            />
            <p class="text-xs text-gray-500 mt-1">Identificador único (ex: minha-empresa)</p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Subdomínio *</label>
            <input
              v-model="form.subdomain"
              type="text"
              required
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Domínio Base *</label>
            <select
              v-model="form.domain_base_option"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="deliveryapphub.com">.deliveryapphub.com</option>
              <option value="deliveryapphub.com.br">.deliveryapphub.com.br</option>
              <option value="custom">Domínio próprio (personalizado)</option>
            </select>
          </div>
          
          <div v-if="form.domain_base_option === 'custom'" class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Domínio Base Próprio *</label>
            <input
              v-model="form.custom_domain_base"
              type="text"
              required
              placeholder="meudominio.com.br"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p class="text-xs text-gray-500 mt-1">Digite o domínio base personalizado. Ex: meudominio.com.br</p>
          </div>
          
          <div class="md:col-span-2">
            <p class="text-sm text-gray-600">
              URL: 
              <span class="font-medium text-orange-600">
                {{ form.subdomain || 'empresa' }}.{{ displayDomainBase }}
              </span>
            </p>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              v-model="form.email"
              type="email"
              required
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
            <input
              v-model="form.phone"
              type="text"
              required
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
            <input
              v-model="form.whatsapp"
              type="text"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <textarea
              v-model="form.description"
              rows="2"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            ></textarea>
          </div>
          
          <!-- Endereço -->
          <div class="md:col-span-2 mt-4">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Endereço</h3>
          </div>
          
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
            <input
              v-model="form.address"
              type="text"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
            <input
              v-model="form.city"
              type="text"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <input
              v-model="form.state"
              type="text"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">CEP</label>
            <div class="relative">
              <input
                v-model="form.zip_code"
                type="text"
                @blur="searchCep"
                @keydown.enter.prevent="searchCep"
                placeholder="00000-000"
                class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-10"
                :class="{ 'border-red-500': cepError }"
              />
              <div v-if="loadingCep" class="absolute right-3 top-1/2 -translate-y-1/2">
                <svg class="animate-spin w-4 h-4 text-orange-600" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <p v-if="cepError" class="text-xs text-red-600 mt-1">{{ cepError }}</p>
          </div>
          
          <!-- Aparência -->
          <div class="md:col-span-2 mt-4">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Aparência</h3>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Cor Primária</label>
            <div class="flex items-center gap-3">
              <input
                v-model="form.primary_color"
                type="color"
                class="w-12 h-10 rounded border cursor-pointer"
              />
              <input
                v-model="form.primary_color"
                type="text"
                class="flex-1 border rounded-lg px-3 py-2 uppercase"
                maxlength="7"
              />
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Cor Secundária</label>
            <div class="flex items-center gap-3">
              <input
                v-model="form.secondary_color"
                type="color"
                class="w-12 h-10 rounded border cursor-pointer"
              />
              <input
                v-model="form.secondary_color"
                type="text"
                class="flex-1 border rounded-lg px-3 py-2 uppercase"
                maxlength="7"
              />
            </div>
          </div>
          
          <!-- Configurações de Delivery -->
          <div class="md:col-span-2 mt-4">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Configurações de Delivery</h3>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Valor Mínimo do Pedido (R$)</label>
            <input
              v-model.number="form.min_order_value"
              type="number"
              min="0"
              step="0.01"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Tempo Médio de Entrega (min)</label>
            <input
              v-model.number="form.average_delivery_time"
              type="number"
              min="1"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <!-- URL do Sistema -->
          <div class="md:col-span-2 mt-4">
            <h3 class="text-lg font-medium text-gray-900 mb-4">Configurações SaaS</h3>
          </div>
          
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Plano de Negócios</label>
            <select
              v-model="form.business_plan_id"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              :disabled="loadingPlans"
            >
              <option :value="null">Selecione um plano</option>
              <option v-for="plan in businessPlans" :key="plan.id" :value="plan.id">
                {{ plan.name || `Plano ${plan.id}` }} ({{ plan.min_order }} - {{ plan.max_order }} pedidos / {{ formatMoney(plan.value) }})
              </option>
            </select>
            <p v-if="loadingPlans" class="text-xs text-gray-500 mt-1">Carregando planos...</p>
          </div>
          
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Domínio Próprio do Cliente</label>
            <input
              v-model="form.custom_domain"
              type="text"
              placeholder="minhaempresa.com"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p class="text-xs text-gray-500 mt-1">Domínio próprio do cliente (opcional). Ex: minhaempresa.com. O cliente deve apontar o DNS para este servidor.</p>
          </div>
          
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">URL do Sistema (retorno de pagamentos)</label>
            <input
              v-model="form.system_url"
              type="url"
              placeholder="https://minhaempresa.com"
              class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p class="text-xs text-gray-500 mt-1">URL usada para retorno de pagamentos (Mercado Pago). Se não informada, usará a URL atual.</p>
          </div>
          
          <!-- Credenciais do Admin (apenas na criação) -->
          <template v-if="!isEdit">
            <div class="md:col-span-2 mt-4">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Credenciais do Administrador</h3>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Nome do Admin *</label>
              <input
                v-model="form.admin_full_name"
                type="text"
                required
                class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email do Admin *</label>
              <input
                v-model="form.admin_email"
                type="email"
                required
                class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Senha do Admin *</label>
              <input
                v-model="form.admin_password"
                type="password"
                required
                minlength="6"
                class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Telefone do Admin</label>
              <input
                v-model="form.admin_phone"
                type="text"
                class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </template>
        </div>
        
        <!-- Actions -->
        <div class="flex justify-end gap-3 mt-8 pt-4 border-t">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            {{ saving ? 'Salvando...' : (isEdit ? 'Salvar' : 'Criar Empresa') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import { companyApi, businessPlanApi } from '@/utils/api'
import { formatMoney } from '@/utils/formatters'
import { notifyError, notifySuccess } from '@/utils/notifications'
import type { Company } from '@/types'

interface BusinessPlan {
  id: number
  name: string | null
  min_order: number
  max_order: number
  value: number
  is_active: boolean
}

const props = defineProps<{
  company: Company | null
  isEdit: boolean
}>()

const emit = defineEmits(['close', 'save'])

const saving = ref(false)
const loadingCep = ref(false)
const cepError = ref('')
const lastSearchedCep = ref('')
const businessPlans = ref<BusinessPlan[]>([])
const loadingPlans = ref(false)

const form = ref({
  name: '',
  slug: '',
  subdomain: '',
  domain_base: 'deliveryapphub.com',
  domain_base_option: 'deliveryapphub.com',
  custom_domain_base: '',
  custom_domain: '',
  description: '',
  email: '',
  phone: '',
  whatsapp: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  primary_color: '#FF6B35',
  secondary_color: '#004E89',
  min_order_value: 0,
  average_delivery_time: 45,
  system_url: '',
  business_plan_id: null as number | null,
  admin_full_name: '',
  admin_email: '',
  admin_password: '',
  admin_phone: ''
})

// Preenche o formulário se estiver editando
watch(() => props.company, (company) => {
  if (company) {
    const isCustomBase = !!company.custom_domain_base
    form.value = {
      ...form.value,
      name: company.name,
      slug: company.slug,
      subdomain: company.subdomain || '',
      domain_base: company.domain_base || 'deliveryapphub.com',
      domain_base_option: isCustomBase ? 'custom' : (company.domain_base || 'deliveryapphub.com'),
      custom_domain_base: company.custom_domain_base || '',
      custom_domain: company.custom_domain || '',
      description: company.description || '',
      email: company.email,
      phone: company.phone,
      whatsapp: company.whatsapp || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      zip_code: company.zip_code || '',
      primary_color: company.primary_color || '#FF6B35',
      secondary_color: company.secondary_color || '#004E89',
      min_order_value: (company.min_order_value || 0) / 100,
      average_delivery_time: company.average_delivery_time || 45,
      system_url: company.system_url || '',
      business_plan_id: company.business_plan_id || null
    }
  }
}, { immediate: true })

const loadBusinessPlans = async () => {
  loadingPlans.value = true
  try {
    const res = await businessPlanApi.getAll()
    businessPlans.value = res.data
  } catch (error) {
    console.error('Erro ao carregar planos:', error)
  } finally {
    loadingPlans.value = false
  }
}

onMounted(() => {
  loadBusinessPlans()
})

const displayDomainBase = computed(() => {
  if (form.value.domain_base_option === 'custom') {
    return form.value.custom_domain_base || 'meudominio.com.br'
  }
  return form.value.domain_base_option
})

const searchCep = async () => {
  const cep = form.value.zip_code.replace(/\D/g, '')
  if (cep.length !== 8) {
    cepError.value = 'CEP deve ter 8 dígitos'
    return
  }
  if (cep === lastSearchedCep.value) return

  loadingCep.value = true
  cepError.value = ''
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
    const data = await response.json()

    if (data.erro) {
      cepError.value = 'CEP não encontrado'
      lastSearchedCep.value = cep
      return
    }

    form.value.address = data.logradouro || form.value.address
    form.value.city = data.localidade || form.value.city
    form.value.state = data.uf || form.value.state
    lastSearchedCep.value = cep
    cepError.value = ''
  } catch (error) {
    console.error('Erro ao buscar CEP:', error)
    cepError.value = 'Erro ao consultar CEP'
  } finally {
    loadingCep.value = false
  }
}

const handleSubmit = async () => {
  // Validação de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(form.value.email)) {
    notifyError('Email inválido')
    return
  }
  
  // Validação de CEP
  const cep = form.value.zip_code.replace(/\D/g, '')
  if (cep.length !== 8) {
    notifyError('CEP inválido. Deve conter 8 dígitos.')
    return
  }
  if (cepError.value) {
    notifyError('CEP inválido: ' + cepError.value)
    return
  }
  
  saving.value = true
  
  // Prepara os campos de domínio base
  const isCustomBase = form.value.domain_base_option === 'custom'
  const domainBaseData = {
    domain_base: isCustomBase ? 'deliveryapphub.com' : form.value.domain_base_option,
    custom_domain_base: isCustomBase ? (form.value.custom_domain_base || null) : null
  }
  
  try {
    if (props.isEdit && props.company) {
      // Atualiza empresa
      await companyApi.update(props.company.id, {
        name: form.value.name,
        slug: form.value.slug,
        subdomain: form.value.subdomain,
        ...domainBaseData,
        custom_domain: form.value.custom_domain || null,
        description: form.value.description,
        email: form.value.email,
        phone: form.value.phone,
        whatsapp: form.value.whatsapp,
        address: form.value.address,
        city: form.value.city,
        state: form.value.state,
        zip_code: form.value.zip_code,
        primary_color: form.value.primary_color,
        secondary_color: form.value.secondary_color,
        min_order_value: Math.round(form.value.min_order_value * 100),
        average_delivery_time: form.value.average_delivery_time,
        system_url: form.value.system_url,
        business_plan_id: form.value.business_plan_id
      })
      notifySuccess('Empresa atualizada com sucesso!')
    } else {
      // Cria empresa com admin
      await companyApi.createWithAdmin({
        name: form.value.name,
        slug: form.value.slug,
        subdomain: form.value.subdomain,
        ...domainBaseData,
        custom_domain: form.value.custom_domain || null,
        description: form.value.description,
        email: form.value.email,
        phone: form.value.phone,
        whatsapp: form.value.whatsapp,
        address: form.value.address,
        city: form.value.city,
        state: form.value.state,
        zip_code: form.value.zip_code,
        primary_color: form.value.primary_color,
        secondary_color: form.value.secondary_color,
        min_order_value: Math.round(form.value.min_order_value * 100),
        average_delivery_time: form.value.average_delivery_time,
        system_url: form.value.system_url,
        business_plan_id: form.value.business_plan_id,
        admin_full_name: form.value.admin_full_name,
        admin_email: form.value.admin_email,
        admin_password: form.value.admin_password,
        admin_phone: form.value.admin_phone
      })
      notifySuccess('Empresa criada com sucesso!')
    }
    
    emit('save')
  } catch (error: any) {
    notifyError('Erro: ' + (error.response?.data?.detail || 'Erro ao salvar empresa'))
  } finally {
    saving.value = false
  }
}
</script>
